import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Dictation (STT): server-side providers first, browser speech as last resort
// ---------------------------------------------------------------------------
//
// Primary path records the mic (MediaRecorder) and POSTs the audio to the
// /api/assistant/transcribe fallback chain (Groq → OpenAI → Gemini → …), which
// is higher quality. The browser's Web Speech recognition is the WORST quality,
// so it's only used when the server has no STT provider (a 501 latches the whole
// session to browser mode) or where MediaRecorder isn't available. A transient
// server failure (502/network) falls back to browser for that one utterance.
//
// Extracted from AssistantStudio so the agent composer shares the exact same
// mic-to-text behaviour. Call toggle(currentText, setText) on a mic button.

interface SpeechRecognitionAlt {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: SpeechRecognitionAlt;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: { length: number; [i: number]: SpeechRecognitionResultLike };
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: unknown) => void) | null;
  start: () => void;
  stop: () => void;
}
function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export type DictationState = 'idle' | 'recording' | 'transcribing';

// Session-wide memory of whether the server can transcribe at all. Once we see a
// 501 ("no provider configured") we stop recording-and-uploading and go straight
// to browser speech — the documented last-resort path.
let serverSTT: 'unknown' | 'ok' | 'down' = 'unknown';

function extractTranscript(payload: unknown): string {
  if (typeof payload === 'string') return payload.trim();
  if (!payload || typeof payload !== 'object') return '';
  const record = payload as Record<string, unknown>;
  for (const key of ['text', 'transcript', 'transcription', 'output']) {
    const text = extractTranscript(record[key]);
    if (text) return text;
  }
  return '';
}

function pickRecorderMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']) {
    if (MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return '';
}

export interface VoiceInput {
  supported: boolean;
  state: DictationState;
  level: number;
  /** Start dictation. `base` is the current text; `onText` receives base+transcript. */
  start: (base: string, onText: (t: string) => void) => void;
  /** Stop the current dictation session and transcribe the captured audio. */
  stop: () => void;
  /** Cancel dictation, discard captured audio, and restore the original text. */
  cancel: () => void;
  /** Start/stop dictation. `base` is the current text; `onText` receives base+transcript. */
  toggle: (base: string, onText: (t: string) => void) => void;
}

export function useVoiceInput(): VoiceInput {
  const [state, setState] = useState<DictationState>('idle');
  const [level, setLevel] = useState(0);
  const browserSupported = useMemo(() => Boolean(getRecognitionCtor()), []);
  const mediaSupported = useMemo(
    () =>
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined',
    [],
  );
  const supported = mediaSupported || browserSupported;

  const onTextRef = useRef<(t: string) => void>(() => {});
  const baseRef = useRef('');
  const originalRef = useRef('');
  const cancelledRef = useRef(false);
  const mrRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const silenceRef = useRef<number>(0);
  const sawSpeechRef = useRef(false);
  const transcribeControllerRef = useRef<AbortController | null>(null);

  const teardownAudio = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    ctxRef.current?.close().catch(() => {});
    ctxRef.current = null;
    setLevel(0);
  }, []);

  // --- browser Web Speech recognition (last resort) ---
  const startBrowser = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setState('idle');
      return;
    }
    const rec = new Ctor();
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
	let finalText = '';
	rec.onresult = (e) => {
	  if (cancelledRef.current || recRef.current !== rec) return;
	  let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      onTextRef.current(baseRef.current + (finalText + interim).replace(/\s+/g, ' ').trimStart());
    };
    rec.onend = () => {
      teardownAudio();
      setState('idle');
      recRef.current = null;
    };
    rec.onerror = () => setState('idle');
    recRef.current = rec;
    rec.start();
    setState('recording');
  }, [teardownAudio]);

  // --- server transcription of a recorded blob ---
  const transcribeBlob = useCallback(
    async (blob: Blob) => {
      if (cancelledRef.current) {
        setState('idle');
        return;
      }
      if (blob.size < 1024) {
        setState('idle');
        return;
      }
      setState('transcribing');
      const controller = new AbortController();
      transcribeControllerRef.current = controller;
      try {
        const res = await fetch('/api/assistant/transcribe', {
          method: 'POST',
          headers: { 'Content-Type': blob.type || 'audio/webm' },
          credentials: 'same-origin',
          body: blob,
          signal: controller.signal,
        });
        if (cancelledRef.current) return;
        if (res.ok) {
          serverSTT = 'ok';
          const data = await res.json().catch(() => ({}));
          const text = extractTranscript(data);
          if (text) onTextRef.current(baseRef.current + text);
          setState('idle');
          return;
        }
        if (res.status === 501) serverSTT = 'down'; // no provider — latch to browser
        // Transient/no-provider failure: retry this utterance with browser speech.
        if (browserSupported) {
          startBrowser();
          return;
        }
      } catch {
        if (cancelledRef.current) return;
        if (browserSupported) {
          startBrowser();
          return;
        }
      } finally {
        if (transcribeControllerRef.current === controller) transcribeControllerRef.current = null;
      }
      setState('idle');
    },
    [browserSupported, startBrowser],
  );

  // --- mic recording with a simple level meter + silence auto-stop ---
  const startRecording = useCallback(async () => {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch {
      // Mic blocked — try browser recognition (it may prompt separately) else give up.
      if (browserSupported) startBrowser();
      else setState('idle');
      return;
    }
    streamRef.current = stream;
    const mime = pickRecorderMime();
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    mr.ondataavailable = (e) => {
      if (e.data.size) chunksRef.current.push(e.data);
    };
    mr.onstop = () => {
      teardownAudio();
      if (cancelledRef.current) {
        chunksRef.current = [];
        setState('idle');
        return;
      }
      const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
      transcribeBlob(blob);
    };
    mrRef.current = mr;
    mr.start();
    setState('recording');

    // Level meter + silence detection (auto-stop ~2.5s after speech ends).
    try {
      const ctx = new AudioContext();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      ctxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      sawSpeechRef.current = false;
      silenceRef.current = 0;
      let last = ctx.currentTime;
      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setLevel(Math.min(1, rms * 18));
        const now = ctx.currentTime;
        const dt = now - last;
        last = now;
        if (rms > 0.015) {
          sawSpeechRef.current = true;
          silenceRef.current = 0;
        } else if (sawSpeechRef.current) {
          silenceRef.current += dt;
          if (silenceRef.current > 2.5 && mrRef.current?.state === 'recording') {
            mrRef.current.stop();
            mrRef.current = null;
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      /* no level meter — manual stop still works */
    }
  }, [browserSupported, startBrowser, teardownAudio, transcribeBlob]);

  const stop = useCallback(() => {
    if (recRef.current) {
      recRef.current.stop();
      return;
    }
    if (mrRef.current && mrRef.current.state === 'recording') {
      mrRef.current.stop();
      mrRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    transcribeControllerRef.current?.abort();
    transcribeControllerRef.current = null;
    onTextRef.current(originalRef.current);
	if (recRef.current) {
	  const rec = recRef.current;
	  recRef.current = null;
	  rec.onresult = null;
	  rec.stop();
    }
    if (mrRef.current?.state === 'recording') {
      const mr = mrRef.current;
      mrRef.current = null;
      mr.stop();
    } else {
      teardownAudio();
      setState('idle');
    }
  }, [teardownAudio]);

  const start = useCallback(
    (base: string, onText: (t: string) => void) => {
      originalRef.current = base;
      baseRef.current = base ? base.replace(/\s+$/, '') + ' ' : '';
      onTextRef.current = onText;
      cancelledRef.current = false;
      if (mediaSupported && serverSTT !== 'down') startRecording();
      else if (browserSupported) startBrowser();
    },
    [mediaSupported, browserSupported, startRecording, startBrowser],
  );

  const toggle = useCallback(
    (base: string, onText: (t: string) => void) => {
      if (state !== 'idle') {
        stop();
        return;
      }
      start(base, onText);
    },
    [state, stop, start],
  );

  useEffect(
    () => () => {
      cancelledRef.current = true;
      transcribeControllerRef.current?.abort();
      recRef.current?.stop();
      if (mrRef.current?.state === 'recording') mrRef.current.stop();
      teardownAudio();
    },
    [teardownAudio],
  );

  return { supported, state, level, start, stop, cancel, toggle };
}
